import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import usePagination from "@/hooks/useSyncedPagination";
import { SortingState } from "@tanstack/react-table";
import { useDebounce } from "@/hooks/useDebounce";
import { useExpeditionFilters } from "@/components/backOffice/pendingExpeditions/useExpeditionFilters.reducer";
import { useGetAllExpeditionBo } from "@/api/expedition.api";
import { useGetCustoms } from "@/api/customs.api";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { ClientExpeditionsFilters } from "@/components/backOffice/expeditionsHistory/clientExpeditionsFilters";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import {
  handleExpeditionRowClick,
  useExpeditionHistoryColumns,
} from "@/components/backOffice/shared/expeditionColumns";
import { toEndOfDayIso, toStartOfDayIso } from "@/lib/utils";

type Props = {
  companyId: string;
};

export const ClientExpeditions = ({ companyId }: Props) => {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>([{ id: "pickupDate", desc: true }]);
  const [pagination, setPagination] = usePagination("clientHistoryBo");
  const { filters, actions } = useExpeditionFilters();
  const debouncedFilters = useDebounce(filters, 300);

  const handleRowClick = useCallback(
    (row: GetAllExpeditionBoResponseDtoExpedition) => handleExpeditionRowClick(row, navigate),
    [navigate],
  );

  const { data: customs = [] } = useGetCustoms();
  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    search: debouncedFilters.search,
    country: debouncedFilters.country,
    ...(debouncedFilters.packagingType && { packagingType: debouncedFilters.packagingType }),
    companyId,
    trackingCode: debouncedFilters.trackingCode,
    includeDeleted: false,
    validatedAfter: debouncedFilters.validatedAt?.[0]
      ? toStartOfDayIso(debouncedFilters.validatedAt[0])
      : undefined,
    validatedBefore: debouncedFilters.validatedAt?.[1]
      ? toEndOfDayIso(debouncedFilters.validatedAt[1])
      : undefined,
    pickupDateAfter: debouncedFilters.pickupDate?.[0]
      ? toStartOfDayIso(debouncedFilters.pickupDate[0])
      : undefined,
    pickupDateBefore: debouncedFilters.pickupDate?.[1]
      ? toEndOfDayIso(debouncedFilters.pickupDate[1])
      : undefined,
    deliveryFromDateAfter: debouncedFilters.deliveryFromDate?.[0]
      ? toStartOfDayIso(debouncedFilters.deliveryFromDate[0])
      : undefined,
    deliveryFromDateBefore: debouncedFilters.deliveryFromDate?.[1]
      ? toEndOfDayIso(debouncedFilters.deliveryFromDate[1])
      : undefined,
    finalizedAfter: debouncedFilters.finalizedAt?.[0]
      ? toStartOfDayIso(debouncedFilters.finalizedAt[0])
      : undefined,
    finalizedBefore: debouncedFilters.finalizedAt?.[1]
      ? toEndOfDayIso(debouncedFilters.finalizedAt[1])
      : undefined,
    deliveryDateAfter: debouncedFilters.deliveryDate?.[0]
      ? toStartOfDayIso(debouncedFilters.deliveryDate[0])
      : undefined,
    deliveryDateBefore: debouncedFilters.deliveryDate?.[1]
      ? toEndOfDayIso(debouncedFilters.deliveryDate[1])
      : undefined,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data || [], [expeditions]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    debouncedFilters.search,
    debouncedFilters.country,
    debouncedFilters.packagingType,
    debouncedFilters.validatedAt,
    debouncedFilters.pickupDate,
    debouncedFilters.deliveryFromDate,
    debouncedFilters.finalizedAt,
    debouncedFilters.deliveryDate,
    debouncedFilters.trackingCode,
    setPagination,
  ]);

  const columns = useExpeditionHistoryColumns(customs, {
    cotationOnly: true,
  });

  return (
    <div className="flex h-fit w-full flex-1 flex-col gap-4">
      <ClientExpeditionsFilters filters={filters} actions={actions} />
      <DataTable
        data={expeditionsData}
        columns={columns}
        pagination={pagination}
        setPagination={setPagination}
        setSorting={setSorting}
        rowCount={expeditions?.count || 0}
        sorting={sorting}
        autoResetPageIndex={false}
        onRowClick={handleRowClick}
        isLoading={isExpeditionsLoading}
        skeletonRowClassName="h-18"
        rowClassname={() => "h-18 bg-white"}
        headerClassName="bg-gray-300"
        headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
      />
    </div>
  );
};
